import React, { useState } from 'react';
import Layout from '@/components/Layout';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Bell, 
  Gift as GiftIcon, 
  CalendarDays, 
  Activity, 
  Home,
  ChartBar,
  Info,
  CalendarIcon,
  X
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { type Gift, type Brand } from '@/utils/types';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import TransactionalGiftCard from '@/components/TransactionalGiftCard';
import { format, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import RecentActivity from '@/components/market/RecentActivity';

// Mock data for activity feed
const MOCK_ACTIVITIES = [
  {
    id: 'act-1',
    type: 'gift_received',
    giftId: 'gift-1',
    senderId: 'user-1',
    senderName: 'John Smith',
    senderAvatar: '/placeholder.svg',
    brandId: 'starbucks',
    amount: 15,
    message: 'Happy birthday! Enjoy a coffee on me!',
    timestamp: new Date(2025, 3, 5, 14, 30)
  },
  {
    id: 'act-2',
    type: 'gift_received',
    giftId: 'gift-2',
    senderId: 'user-2',
    senderName: 'Sarah Johnson',
    senderAvatar: '/placeholder.svg',
    brandId: 'mcdonalds',
    amount: 10,
    message: 'Congrats on your promotion!',
    timestamp: new Date(2025, 3, 5, 12, 15)
  },
  {
    id: 'act-3',
    type: 'gift_redeemed',
    giftId: 'gift-old-1',
    brandId: 'burgerking',
    amount: 15,
    timestamp: new Date(2025, 3, 4, 18, 45)
  },
  {
    id: 'act-4',
    type: 'gift_sent',
    recipientId: 'user-3',
    recipientName: 'Mike Wilson',
    recipientAvatar: '/placeholder.svg',
    brandId: 'dunkin',
    amount: 5,
    message: 'For helping me with the project!',
    timestamp: new Date(2025, 3, 4, 10, 20)
  },
  {
    id: 'act-5',
    type: 'gift_received',
    giftId: 'gift-3',
    senderId: 'user-4',
    senderName: 'Emma Davis',
    senderAvatar: '/placeholder.svg',
    brandId: 'starbucks',
    amount: 10,
    timestamp: new Date(2025, 3, 3, 16, 10)
  },
  {
    id: 'act-6',
    type: 'special_day_notification',
    specialDay: 'birthday',
    timestamp: new Date(2025, 3, 3, 9, 0)
  },
  {
    id: 'act-7',
    type: 'gift_exchanged',
    oldBrandId: 'cvs',
    newBrandId: 'xbox',
    amount: 15,
    timestamp: new Date(2025, 3, 2, 14, 35)
  },
  {
    id: 'act-8',
    type: 'gift_received',
    giftId: 'gift-4',
    senderId: 'user-5',
    senderName: 'Alex Garcia',
    senderAvatar: '/placeholder.svg',
    brandId: 'walmart',
    amount: 15,
    message: 'Happy anniversary!',
    timestamp: new Date(2025, 3, 1, 11, 25)
  }
];

// Updated paths using placeholder images that are included in all Vite projects
const MOCK_BRANDS: Brand[] = [
  // Restaurants
  { id: 'mcdonalds', name: 'McDonald\'s', logo: '/placeholder.svg', silo: 'restaurants' },
  { id: 'burgerking', name: 'Burger King', logo: '/placeholder.svg', silo: 'restaurants' },
  { id: 'chickfila', name: 'Chick-fil-A', logo: '/placeholder.svg', silo: 'restaurants' },
  { id: 'tacobell', name: 'Taco Bell', logo: '/placeholder.svg', silo: 'restaurants' },
  
  // Beverages
  { id: 'starbucks', name: 'Starbucks', logo: '/placeholder.svg', silo: 'beverages' },
  { id: 'dunkin', name: 'Dunkin\' Donuts', logo: '/placeholder.svg', silo: 'beverages' },
  { id: 'krispy', name: 'Krispy Kreme', logo: '/placeholder.svg', silo: 'beverages' },
  { id: 'timhortons', name: 'Tim Horton\'s', logo: '/placeholder.svg', silo: 'beverages' },
  
  // Convenience
  { id: 'walmart', name: 'Walmart', logo: '/placeholder.svg', silo: 'convenience' },
  { id: 'cvs', name: 'CVS', logo: '/placeholder.svg', silo: 'convenience' },
  { id: 'riteaid', name: 'Rite Aid', logo: '/placeholder.svg', silo: 'convenience' },
  { id: 'walgreens', name: 'Walgreens', logo: '/placeholder.svg', silo: 'convenience' },
  
  // Entertainment
  { id: 'amc', name: 'AMC Theatres', logo: '/placeholder.svg', silo: 'entertainment' },
  { id: 'showcase', name: 'Showcase Cinemas', logo: '/placeholder.svg', silo: 'entertainment' },
  { id: 'xbox', name: 'Xbox', logo: '/placeholder.svg', silo: 'entertainment' },
  { id: 'playstation', name: 'PlayStation', logo: '/placeholder.svg', silo: 'entertainment' },
];

const getBrandName = (brandId: string) => {
  const brand = MOCK_BRANDS.find(b => b.id === brandId);
  return brand ? brand.name : 'Unknown Brand';
};

const ActivityFeed = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  
  const handleBack = () => {
    navigate('/');
  };
  
  const formatDate = (date: Date) => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };
  
  const clearDateFilter = () => {
    setDateFrom(undefined);
    setDateTo(undefined);
  };
  
  const getFilteredActivities = () => {
    let filtered = MOCK_ACTIVITIES;
    
    if (activeTab === 'all') filtered = MOCK_ACTIVITIES;
    else if (activeTab === 'received') filtered = MOCK_ACTIVITIES.filter(act => act.type === 'gift_received');
    else if (activeTab === 'sent') filtered = MOCK_ACTIVITIES.filter(act => act.type === 'gift_sent');
    else if (activeTab === 'redeemed') filtered = MOCK_ACTIVITIES.filter(act => 
      act.type === 'gift_redeemed' || act.type === 'gift_exchanged'
    );

    if (dateFrom && dateTo) {
      filtered = filtered.filter(activity => {
        const activityDate = activity.timestamp;
        return isWithinInterval(activityDate, {
          start: startOfDay(dateFrom),
          end: endOfDay(dateTo)
        });
      });
    } else if (dateFrom) {
      filtered = filtered.filter(activity => {
        const activityDate = activity.timestamp;
        return activityDate >= startOfDay(dateFrom);
      });
    } else if (dateTo) {
      filtered = filtered.filter(activity => {
        const activityDate = activity.timestamp;
        return activityDate <= endOfDay(dateTo);
      });
    }
    
    return filtered;
  };
  
  const getActivityIcon = (activity: any) => {
    switch (activity.type) {
      case 'gift_received':
        return <GiftIcon className="h-6 w-6 text-green-500" />;
      case 'gift_sent':
        return <GiftIcon className="h-6 w-6 text-blue-500" />;
      case 'gift_redeemed':
        return <Activity className="h-6 w-6 text-orange-500" />;
      case 'gift_exchanged':
        return <Activity className="h-6 w-6 text-purple-500" />;
      case 'special_day_notification':
        return <CalendarDays className="h-6 w-6 text-pink-500" />;
      default:
        return <Bell className="h-6 w-6 text-gray-500" />;
    }
  };
  
  const getActivityContent = (activity: any) => {
    switch (activity.type) {
      case 'gift_received':
        return (
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-1">
              <Avatar className="h-6 w-6">
                <AvatarImage src={activity.senderAvatar} />
                <AvatarFallback>{activity.senderName[0]}</AvatarFallback>
              </Avatar>
              <p className="font-medium">{activity.senderName}</p>
              <span className="text-muted-foreground text-xs">sent you</span>
              <Badge variant="outline" className="bg-green-50">
                ${activity.amount} {getBrandName(activity.brandId)}
              </Badge>
            </div>
            {activity.message && (
              <div className="bg-gray-50 p-2 rounded-md text-sm mt-1 italic">
                "{activity.message}"
              </div>
            )}
          </div>
        );
      case 'gift_sent':
        return (
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm">You sent</span>
              <Badge variant="outline" className="bg-blue-50">
                ${activity.amount} {getBrandName(activity.brandId)}
              </Badge>
              <span className="text-muted-foreground text-xs">to</span>
              <Avatar className="h-6 w-6">
                <AvatarImage src={activity.recipientAvatar} />
                <AvatarFallback>{activity.recipientName[0]}</AvatarFallback>
              </Avatar>
              <p className="font-medium">{activity.recipientName}</p>
            </div>
            {activity.message && (
              <div className="bg-gray-50 p-2 rounded-md text-sm mt-1 italic">
                "{activity.message}"
              </div>
            )}
          </div>
        );
      case 'gift_redeemed':
        return (
          <div className="flex items-center gap-2">
            <p>You redeemed</p>
            <Badge variant="outline" className="bg-orange-50">
              ${activity.amount} {getBrandName(activity.brandId)}
            </Badge>
          </div>
        );
      case 'gift_exchanged':
        return (
          <div className="flex items-center gap-2 flex-wrap">
            <p>You exchanged</p>
            <Badge variant="outline" className="bg-gray-100">
              ${activity.amount} {getBrandName(activity.oldBrandId)}
            </Badge>
            <span>for</span>
            <Badge variant="outline" className="bg-purple-50">
              ${activity.amount} {getBrandName(activity.newBrandId)}
            </Badge>
          </div>
        );
      case 'special_day_notification':
        return (
          <div className="flex items-center gap-2">
            <p>Your network was notified about your {activity.specialDay}!</p>
          </div>
        );
      default:
        return <p>Unknown activity</p>;
    }
  };
  
  const filteredActivities = getFilteredActivities();
  
  const totalReceived = MOCK_ACTIVITIES.filter(a => a.type === 'gift_received').reduce(
    (sum, act) => sum + (act.amount || 0), 0
  );
  
  const totalSent = MOCK_ACTIVITIES.filter(a => a.type === 'gift_sent').reduce(
    (sum, act) => sum + (act.amount || 0), 0
  );
  
  const today = new Date();
  const receivedToday = MOCK_ACTIVITIES.filter(
    a => a.type === 'gift_received' && a.timestamp.toDateString() === today.toDateString()
  ).length;

  const giftsReceivedToday = MOCK_ACTIVITIES.filter(
    a => a.type === 'gift_received' && a.timestamp.toDateString() === today.toDateString()
  );

  const giftsSentData = MOCK_ACTIVITIES.filter(a => a.type === 'gift_sent');
  const recipientSet = new Set(giftsSentData.map(g => g.recipientId));
  const uniqueRecipients = Array.from(recipientSet).map(id => {
    const gift = giftsSentData.find(g => g.recipientId === id);
    return {
      id,
      name: gift?.recipientName || 'Unknown',
      avatar: gift?.recipientAvatar || '/placeholder.svg',
      giftsCount: giftsSentData.filter(g => g.recipientId === id).length,
      totalAmount: giftsSentData.filter(g => g.recipientId === id)
        .reduce((sum, gift) => sum + gift.amount, 0)
    };
  });

  const brandBalances = MOCK_BRANDS.map(brand => {
    const received = MOCK_ACTIVITIES
      .filter(a => (a.type === 'gift_received' || a.type === 'gift_exchanged' && a.newBrandId === brand.id) 
              && (a.brandId === brand.id || a.newBrandId === brand.id))
      .reduce((sum, act) => sum + (act.amount || 0), 0);
    
    const spent = MOCK_ACTIVITIES
      .filter(a => (a.type === 'gift_redeemed' || a.type === 'gift_exchanged' && a.oldBrandId === brand.id) 
              && (a.brandId === brand.id || a.oldBrandId === brand.id))
      .reduce((sum, act) => sum + (act.amount || 0), 0);
    
    return {
      ...brand,
      balance: received - spent
    };
  }).filter(b => b.balance > 0);
  
  const totalAvailableBalance = brandBalances.reduce((sum, brand) => sum + brand.balance, 0);
  
  return (
    <Layout>
      <div className="pb-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Activity Feed</h1>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleBack}
            className="flex items-center gap-2"
          >
            <Home className="h-4 w-4" /> Home
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card className="bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center">
                <GiftIcon className="h-12 w-12 text-green-600 mr-3" />
                <div>
                  <p className="text-sm text-muted-foreground">Total Received</p>
                  <p className="text-3xl font-bold">${totalReceived}</p>
                  <Popover>
                    <PopoverTrigger asChild>
                      <p className="text-sm text-green-600 cursor-pointer hover:underline flex items-center">
                        {receivedToday} gifts today <Info size={12} className="ml-1" />
                      </p>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <div className="space-y-2">
                        <h3 className="font-medium">Gifts Received Today</h3>
                        {giftsReceivedToday.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No gifts received today</p>
                        ) : (
                          <div className="space-y-3 max-h-64 overflow-y-auto">
                            {giftsReceivedToday.map(gift => (
                              <div key={gift.id} className="flex items-start gap-2 p-2 border rounded-md bg-white">
                                <Avatar className="h-8 w-8">
                                  <AvatarImage src={gift.senderAvatar} />
                                  <AvatarFallback>{gift.senderName[0]}</AvatarFallback>
                                </Avatar>
                                <div>
                                  <div className="flex items-center gap-1">
                                    <p className="text-sm font-medium">{gift.senderName}</p>
                                    <span className="text-xs text-muted-foreground">sent</span>
                                    <Badge variant="outline" className="bg-green-50 text-xs">
                                      ${gift.amount} {getBrandName(gift.brandId)}
                                    </Badge>
                                  </div>
                                  {gift.message && (
                                    <p className="text-xs italic mt-1">"{gift.message}"</p>
                                  )}
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {formatDate(gift.timestamp)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-center">
                <GiftIcon className="h-12 w-12 text-blue-600 mr-3" />
                <div>
                  <p className="text-sm text-muted-foreground">Total Gifted</p>
                  <p className="text-3xl font-bold">${totalSent}</p>
                  <Popover>
                    <PopoverTrigger asChild>
                      <p className="text-sm text-blue-600 cursor-pointer hover:underline flex items-center">
                        To {uniqueRecipients.length} friends <Info size={12} className="ml-1" />
                      </p>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <div className="space-y-2">
                        <h3 className="font-medium">Gifts Sent to Friends</h3>
                        {uniqueRecipients.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No gifts sent yet</p>
                        ) : (
                          <div className="space-y-3 max-h-64 overflow-y-auto">
                            {uniqueRecipients.map(recipient => (
                              <div key={recipient.id} className="flex items-center justify-between p-2 border rounded-md bg-white">
                                <div className="flex items-center gap-2">
                                  <Avatar className="h-8 w-8">
                                    <AvatarImage src={recipient.avatar} />
                                    <AvatarFallback>{recipient.name[0]}</AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <p className="text-sm font-medium">{recipient.name}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {recipient.giftsCount} gift{recipient.giftsCount !== 1 ? 's' : ''}
                                    </p>
                                  </div>
                                </div>
                                <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">
                                  ${recipient.totalAmount}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-purple-50">
            <CardContent className="pt-6">
              <div className="flex items-center">
                <ChartBar className="h-12 w-12 text-purple-600 mr-3" />
                <div>
                  <p className="text-sm text-muted-foreground">Available Balance</p>
                  <p className="text-3xl font-bold">${totalAvailableBalance}</p>
                  <Popover>
                    <PopoverTrigger asChild>
                      <p className="text-sm text-purple-600 cursor-pointer hover:underline flex items-center">
                        Across {brandBalances.length} brands <Info size={12} className="ml-1" />
                      </p>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <div className="space-y-2">
                        <h3 className="font-medium">Balance by Brand</h3>
                        {brandBalances.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No balance available</p>
                        ) : (
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {brandBalances.map(brand => (
                              <div key={brand.id} className="flex items-center justify-between p-2 border rounded-md bg-white">
                                <div className="flex items-center gap-2">
                                  <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                                    <img src={brand.logo} alt={brand.name} className="h-6 w-6" />
                                  </div>
                                  <p className="text-sm font-medium">{brand.name}</p>
                                </div>
                                <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-200">
                                  ${brand.balance}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        <RecentActivity />
        
        <Card className="mt-8">
          <CardHeader className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <CardTitle>Recent Activity</CardTitle>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full sm:w-auto justify-start text-left font-normal"
                      onClick={() => setIsCalendarOpen(true)}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateFrom && dateTo ? (
                        <>
                          {format(dateFrom, "MMM d, yyyy")} - {format(dateTo, "MMM d, yyyy")}
                        </>
                      ) : dateFrom ? (
                        <>From {format(dateFrom, "MMM d, yyyy")}</>
                      ) : dateTo ? (
                        <>Until {format(dateTo, "MMM d, yyyy")}</>
                      ) : (
                        <span>Select Date Range</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="p-3 border-b">
                      <div className="flex justify-between items-center">
                        <h3 className="font-medium text-sm">Date Range</h3>
                        {(dateFrom || dateTo) && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6" 
                            onClick={clearDateFilter}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <div className="flex gap-2 mt-2 text-xs">
                        <div>
                          <div className="text-muted-foreground">From</div>
                          <div>{dateFrom ? format(dateFrom, "MMM d, yyyy") : "Select"}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">To</div>
                          <div>{dateTo ? format(dateTo, "MMM d, yyyy") : "Select"}</div>
                        </div>
                      </div>
                    </div>
                    <Calendar
                      mode="range"
                      selected={{
                        from: dateFrom,
                        to: dateTo,
                      }}
                      onSelect={(range) => {
                        setDateFrom(range?.from);
                        setDateTo(range?.to);
                      }}
                      numberOfMonths={1}
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
                
                {(dateFrom || dateTo) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-1 sm:mt-0"
                    onClick={clearDateFilter}
                  >
                    Clear Dates
                  </Button>
                )}
              </div>
            </div>
            
            <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">All Activity</TabsTrigger>
                <TabsTrigger value="received">Received</TabsTrigger>
                <TabsTrigger value="sent">Sent</TabsTrigger>
                <TabsTrigger value="redeemed">Redeemed</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filteredActivities.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  {dateFrom || dateTo ? 
                    "No activities found for the selected date range" : 
                    "No activities to display"}
                </div>
              ) : (
                filteredActivities.map((activity) => (
                  <div 
                    key={activity.id}
                    className="flex items-start gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-shrink-0">
                      {getActivityIcon(activity)}
                    </div>
                    <div className="flex-1">
                      {getActivityContent(activity)}
                      <div className="text-xs text-muted-foreground mt-2">
                        {formatDate(activity.timestamp)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ActivityFeed;
